import { openPencilColorToHex, type OpenPencilRgbColor } from "./fill.ts";

/**
 * Paint-level variable bindings recovered from the RETAINED Figma raw payload.
 *
 * WHY THIS MODULE EXISTS (measured, not assumed)
 * ----------------------------------------------
 * `SceneNode.boundVariables` cannot express a Figma-authored paint→variable
 * binding, so any safety claim resting on it is vacuous for real customer
 * `.fig` files. Two independent code paths in `@open-pencil/core@0.13.2` say so:
 *
 * 1. `dist/kiwi/fig/node-change/plugin-data.js` — `extractBoundVariables()`
 *    populates `boundVariables["fills/N/color"]` ONLY from
 *    `paint.colorVariableBinding`, a field that exists exclusively in
 *    OpenPencil's OWN re-encoded output (`codec/variable-bindings.js` hand-
 *    injects it). Figma writes the binding as `paint.colorVar` instead.
 * 2. `dist/kiwi/fig/import.js` — `importVariableBindings()` maps node-level
 *    `variableConsumptionMap` entries through `VARIABLE_BINDING_FIELDS`, whose
 *    key set is corner radii / stroke weight / spacing / padding / visible /
 *    opacity / width / height / font size / letter spacing / line height. It
 *    contains NO fill or stroke colour field.
 *
 * Measured on `[공통] 영업점검색_UI.fig` (93,240 nodes): `boundVariables`
 * entries = 0, while `source.fig.rawNodeFields.{fillPaints,strokePaints}[i]
 * .colorVar` yields 30,243 bindings (0 unresolvable aliases).
 *
 * WHY THE BINDINGS STILL MATTER AT EXPORT TIME
 * -------------------------------------------
 * The parser RESOLVES the alias into the materialized colour and then drops it
 * (`node-change/paint.js` → `convertBaseFill`: `color: convertColor(
 * resolveColorVar(p) ?? p.color)`), while the raw payload keeps the alias
 * (`node-change/convert.js` → `FIGMA_RAW_NODE_FIELD_KEYS` includes
 * `fillPaints`/`strokePaints`) and the writer re-emits the raw paints verbatim
 * for source-backed nodes (`node-change/export-node.js` →
 * `applyRawFigmaNodeFields`). So changing a variable's mode value changes the
 * colour that a bound paint materializes to on the NEXT parse — a node-level,
 * parity-visible change produced by an edit that only touched the variable
 * table.
 *
 * This module derives that binding graph from the retained payload and mirrors
 * the importer's own resolution order, so both facts can be enforced instead of
 * asserted: the fill-edit refusal (which paint is bound) and the change-
 * isolation expectation (which node/paint the variable edit re-materializes).
 */

/** Kiwi GUID as retained in the raw payload. */
export interface OpenPencilVariableGuidLike {
  sessionID?: number | null;
  localID?: number | null;
}

/**
 * Alias reference on a retained paint. Figma-authored files use `assetRef`
 * (library key/version); OpenPencil-authored files use `guid`. Both forms are
 * resolved here exactly as `import.js#resolveAliasId` resolves them.
 */
export interface OpenPencilVariableAliasLike {
  guid?: OpenPencilVariableGuidLike | null;
  assetRef?: { key?: string | null; version?: string | null } | null;
}

export interface OpenPencilRawPaintLike {
  colorVar?: {
    value?: { alias?: OpenPencilVariableAliasLike | null } | null;
  } | null;
}

export interface OpenPencilBindingNodeLike {
  id: string;
  source?:
    | {
        id?: string | null;
        fig?: { rawNodeFields?: Record<string, unknown> | null } | null;
      }
    | null
    | undefined;
  boundVariables?: Record<string, string> | null;
  fills?: ReadonlyArray<{ color?: OpenPencilRgbColor | null }> | null;
  strokes?: ReadonlyArray<{ color?: OpenPencilRgbColor | null }> | null;
}

export interface OpenPencilBindingVariableLike {
  id: string;
  collectionId: string;
  valuesByMode: Record<string, unknown>;
  key?: string | null;
  version?: string | null;
}

export interface OpenPencilBindingCollectionLike {
  id: string;
  modes: ReadonlyArray<{ modeId: string }>;
}

export type OpenPencilPaintChannel = "fills" | "strokes";

/** `fills` → `fillPaints`, `strokes` → `strokePaints` in the raw payload. */
const RAW_PAINT_KEY: Record<OpenPencilPaintChannel, string> = {
  fills: "fillPaints",
  strokes: "strokePaints",
};

/** Parity record field each channel materializes into. */
export const PAINT_CHANNEL_PARITY_FIELD: Record<
  OpenPencilPaintChannel,
  "fillsSignature" | "strokesSignature"
> = {
  fills: "fillsSignature",
  strokes: "strokesSignature",
};

export interface OpenPencilPaintVariableBinding {
  nodeId: string;
  sourceId: string | null;
  channel: OpenPencilPaintChannel;
  index: number;
  variableId: string;
}

export interface OpenPencilPaintRematerialization
  extends OpenPencilPaintVariableBinding {
  /** Colour the paint materializes to under the BEFORE variable table. */
  beforeColorHex: string;
  /** Colour the paint materializes to under the AFTER variable table. */
  afterColorHex: string;
}

function guidToString(
  guid: OpenPencilVariableGuidLike | null | undefined,
): string | null {
  if (!guid) return null;
  if (typeof guid.sessionID !== "number" || typeof guid.localID !== "number") {
    return null;
  }
  return `${guid.sessionID}:${guid.localID}`;
}

/**
 * `key` and `key@version` → variable id, mirroring `import.js#buildAssetRefMap`
 * (which additionally indexes `userFacingVersion`; `resolveAliasId` falls back
 * to the bare `key`, so the bare-key entry covers that case).
 */
export function buildOpenPencilVariableAssetRefIndex(
  variables: Iterable<OpenPencilBindingVariableLike>,
): Map<string, string> {
  const index = new Map<string, string>();
  for (const variable of variables) {
    const key = variable.key;
    if (typeof key !== "string" || key.length === 0) continue;
    index.set(key, variable.id);
    if (typeof variable.version === "string" && variable.version.length > 0) {
      index.set(`${key}@${variable.version}`, variable.id);
    }
  }
  return index;
}

/** Mirrors `import.js#resolveAliasId` (guid first, then `key@version`, then `key`). */
export function resolveOpenPencilVariableAlias(
  alias: OpenPencilVariableAliasLike | null | undefined,
  assetRefIndex: ReadonlyMap<string, string>,
): string | null {
  if (!alias) return null;
  const guid = guidToString(alias.guid);
  if (guid) return guid;
  const key = alias.assetRef?.key;
  if (typeof key !== "string" || key.length === 0) return null;
  const version = alias.assetRef?.version;
  const versioned =
    typeof version === "string" && version.length > 0
      ? assetRefIndex.get(`${key}@${version}`)
      : undefined;
  return versioned ?? assetRefIndex.get(key) ?? null;
}

function rawPaints(
  node: OpenPencilBindingNodeLike,
  channel: OpenPencilPaintChannel,
): OpenPencilRawPaintLike[] {
  const raw = node.source?.fig?.rawNodeFields;
  if (!raw) return [];
  const paints = raw[RAW_PAINT_KEY[channel]];
  return Array.isArray(paints) ? (paints as OpenPencilRawPaintLike[]) : [];
}

/**
 * Variable id bound to one paint, from EITHER binding representation:
 * OpenPencil's own `boundVariables` table or the retained Figma raw payload.
 * Returns `null` when the paint carries no variable binding.
 */
export function openPencilPaintVariableBindingId(
  node: OpenPencilBindingNodeLike,
  channel: OpenPencilPaintChannel,
  index: number,
  assetRefIndex: ReadonlyMap<string, string>,
): string | null {
  const native = node.boundVariables?.[`${channel}/${index}/color`];
  if (typeof native === "string" && native.length > 0) return native;
  const alias = rawPaints(node, channel)[index]?.colorVar?.value?.alias;
  return resolveOpenPencilVariableAlias(alias, assetRefIndex);
}

/**
 * Every paint→variable binding in the graph. Bindings are emitted for the raw
 * payload index even when the live paint list is shorter — callers that reason
 * about parity signatures must intersect with the live paint list themselves
 * (see {@link predictOpenPencilPaintRematerializations}).
 */
export function collectOpenPencilPaintVariableBindings(
  nodes: Iterable<OpenPencilBindingNodeLike>,
  assetRefIndex: ReadonlyMap<string, string>,
): OpenPencilPaintVariableBinding[] {
  const bindings: OpenPencilPaintVariableBinding[] = [];
  for (const node of nodes) {
    for (const channel of ["fills", "strokes"] as const) {
      const paints = rawPaints(node, channel);
      const nativeCount = Object.keys(node.boundVariables ?? {}).length;
      const limit = Math.max(
        paints.length,
        nativeCount > 0 ? (node[channel]?.length ?? 0) : 0,
      );
      for (let index = 0; index < limit; index += 1) {
        const variableId = openPencilPaintVariableBindingId(
          node,
          channel,
          index,
          assetRefIndex,
        );
        if (!variableId) continue;
        bindings.push({
          nodeId: node.id,
          sourceId: node.source?.id ?? null,
          channel,
          index,
          variableId,
        });
      }
    }
  }
  return bindings;
}

export interface OpenPencilVariableTableLike {
  variables: ReadonlyMap<string, OpenPencilBindingVariableLike>;
  collections: ReadonlyMap<string, OpenPencilBindingCollectionLike>;
}

function isRgbColor(value: unknown): value is OpenPencilRgbColor {
  return (
    !!value &&
    typeof value === "object" &&
    typeof (value as OpenPencilRgbColor).r === "number" &&
    typeof (value as OpenPencilRgbColor).g === "number" &&
    typeof (value as OpenPencilRgbColor).b === "number"
  );
}

function isAlias(value: unknown): value is { aliasId: string } {
  return (
    !!value &&
    typeof value === "object" &&
    typeof (value as { aliasId?: unknown }).aliasId === "string"
  );
}

/**
 * Resolve a COLOR variable to the hex the parser materializes into a bound
 * paint, mirroring `import.js#buildVariableColorResolver`:
 *
 *   preferred mode (only when the variable actually holds that mode)
 *   → the collection's FIRST declared mode (`variableSetModes[0]`, which
 *     `importCollections` also stores as `defaultModeId`)
 *   → the first value present
 *
 * and recursing through aliases with the consumed mode as the next preference,
 * bounded at the same depth 10.
 *
 * The hex is produced with {@link openPencilColorToHex} — the SAME projection
 * the parity oracle applies to a materialized fill — so a difference here is
 * exactly a difference the parity signature can observe (alpha-only variable
 * changes are invisible to both, by construction).
 */
export function resolveOpenPencilVariableColorHex(
  table: OpenPencilVariableTableLike,
  variableId: string,
  preferredModeId?: string,
  depth = 0,
): string | null {
  if (depth > 10) return null;
  const variable = table.variables.get(variableId);
  if (!variable) return null;
  const collection = table.collections.get(variable.collectionId);
  const firstCollectionMode = collection?.modes[0]?.modeId;
  const modeId =
    preferredModeId !== undefined &&
    Object.hasOwn(variable.valuesByMode, preferredModeId)
      ? preferredModeId
      : firstCollectionMode !== undefined &&
          Object.hasOwn(variable.valuesByMode, firstCollectionMode)
        ? firstCollectionMode
        : Object.keys(variable.valuesByMode)[0];
  if (modeId === undefined) return null;
  const value = variable.valuesByMode[modeId];
  if (isAlias(value)) {
    return resolveOpenPencilVariableColorHex(
      table,
      value.aliasId,
      modeId,
      depth + 1,
    );
  }
  return isRgbColor(value) ? openPencilColorToHex(value) : null;
}

export interface PredictOpenPencilPaintRematerializationsInput {
  /** Nodes of the graph the revision exports (post-patch node state). */
  nodes: Iterable<OpenPencilBindingNodeLike>;
  /** Variable table as it stood BEFORE the bounded patch. */
  before: OpenPencilVariableTableLike;
  /** Variable table as it stands AFTER the bounded patch. */
  after: OpenPencilVariableTableLike;
}

/**
 * Paints whose materialized colour changes because a variable-mode value
 * changed — i.e. exactly the node-level parity changes a `set-variable-mode-
 * value` edit produces on reopen, computed with the parser's own resolution.
 *
 * Deliberately narrow:
 *  - Only paints that EXIST in the live paint list are reported; a raw-payload
 *    paint with no live counterpart cannot move a parity signature.
 *  - Only a change in the parity colour projection counts. A variable edit that
 *    resolves to the same `#rrggbb` (an alpha-only change, for example) yields
 *    no expectation, because the parity signature would show none and an
 *    unfulfilled expectation is itself a fail-closed error.
 *  - Effects/background paints are excluded on purpose: `convertEffects` in
 *    `node-change/paint.js` never resolves `colorVar`, so they cannot
 *    re-materialize.
 */
export function predictOpenPencilPaintRematerializations(
  input: PredictOpenPencilPaintRematerializationsInput,
): OpenPencilPaintRematerialization[] {
  const assetRefIndex = buildOpenPencilVariableAssetRefIndex(
    input.before.variables.values(),
  );
  const beforeHexCache = new Map<string, string | null>();
  const afterHexCache = new Map<string, string | null>();
  const resolve = (
    table: OpenPencilVariableTableLike,
    cache: Map<string, string | null>,
    variableId: string,
  ): string | null => {
    const cached = cache.get(variableId);
    if (cached !== undefined) return cached;
    const resolved = resolveOpenPencilVariableColorHex(table, variableId);
    cache.set(variableId, resolved);
    return resolved;
  };

  const rematerializations: OpenPencilPaintRematerialization[] = [];
  for (const node of input.nodes) {
    for (const binding of collectOpenPencilPaintVariableBindings(
      [node],
      assetRefIndex,
    )) {
      const livePaints = node[binding.channel];
      if (!livePaints || binding.index >= livePaints.length) continue;
      const beforeHex = resolve(
        input.before,
        beforeHexCache,
        binding.variableId,
      );
      const afterHex = resolve(input.after, afterHexCache, binding.variableId);
      if (beforeHex === null || afterHex === null) continue;
      if (beforeHex === afterHex) continue;
      rematerializations.push({
        ...binding,
        beforeColorHex: beforeHex,
        afterColorHex: afterHex,
      });
    }
  }
  return rematerializations;
}
